import type { PlaceBetResp, PlaceBetResult } from '@swf/sports/types/place-bet';
import { buildOpportunityId } from '@swf/sports/utils/buildOpportunityId';
import { getBetNoFromOpportunity } from '@swf/sports/utils/getBetNoFromOpportunity';

import { initialState } from '../../betSlip';
import type {
  BetReceipt,
  BetSlipState,
  MultipleOpportunity,
  MultipleRunner,
  OpportunitiesInput,
  SingleAvailableOpportunity
} from '../../types/betSlip';
import { isMultipleOpportunity } from '../../utils/betSlip/isOpportunity';

const getMultipleDetails = (
  opportunity: MultipleOpportunity,
  state: BetSlipState
): MultipleRunner[] =>
  opportunity.runnersIds.map((runnerId) => {
    const { description, name, handicap, winRunnerOdds, placeTerms } =
      state.opportunities.SINGLE.find(
        ({ id }) => buildOpportunityId('SINGLE', [runnerId]) === id
      ) as SingleAvailableOpportunity;

    return { ...runnerId, description, name, handicap, winRunnerOdds, placeTerms };
  });

const generateReceiptItem =
  (data: PlaceBetResult[], state: BetSlipState, formValues: OpportunitiesInput) =>
  <T extends SingleAvailableOpportunity | MultipleOpportunity>(opportunity: T): T => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { betId, betReceiptId, totalStake, totalPotentialWin, betPrice, legs } = data.find(
      (result) => result.betNo === getBetNoFromOpportunity({ ...opportunity })
    )!;

    const { eachWaySelected } = formValues[opportunity.id] || {};

    return {
      ...opportunity,
      betId,
      betReceiptId,
      totalStake: (totalStake || 0) * 100,
      totalPotentialWin: (totalPotentialWin || 0) * 100,
      legs,
      betPrice,
      eachWaySelected,
      runners: isMultipleOpportunity(opportunity)
        ? getMultipleDetails(opportunity, state)
        : undefined
    };
  };

const betPlacedSuccessfully = (
  state: BetSlipState,
  data: {
    opportunities: BetSlipState['opportunities'];
    formValues: OpportunitiesInput;
    response: PlaceBetResp;
  }
): BetSlipState => {
  const {
    response: { result },
    formValues,
    opportunities
  } = data;
  if (!result) return state;

  const singleAvailableOpportunity = opportunities.SINGLE as SingleAvailableOpportunity[];
  const receipt: BetReceipt = {
    SINGLE: singleAvailableOpportunity.map(generateReceiptItem(result, state, formValues)),
    MULTIPLE: opportunities.MULTIPLE.map(generateReceiptItem(result, state, formValues))
  };

  return {
    ...initialState,
    receipt,
    submittedOpportunities: state.opportunities
  };
};

export default betPlacedSuccessfully;
